// Whole-word, case-insensitive term matching in the Automate prompt, plus the
// insert/remove half of the toggle contract used by every clickable name on the
// screen: Workspace app icons, and the file / tab / header parts of the sheet
// graphic. Click once to put the name in the prompt, click again to take the
// nearest one back out.

const WORD_CHAR = /[A-Za-z0-9_]/;

function isWordBoundary(text, index, length) {
  const before = text[index - 1];
  const after = text[index + length];
  return !WORD_CHAR.test(before || '') && !WORD_CHAR.test(after || '');
}

// Case-insensitive indexOf; returns -1 when missing.
function indexOfIgnoreCase(haystack, needle, fromIndex = 0) {
  const source = String(haystack || '');
  const target = String(needle || '');
  if (!target) return -1;
  return source.toLowerCase().indexOf(target.toLowerCase(), fromIndex);
}

// Every case-insensitive whole-word hit for any of the terms, in text order.
export function findTermMatches(text, terms) {
  const source = String(text || '');
  const list = Array.isArray(terms) ? terms : [terms];
  const matches = [];

  for (const term of list) {
    if (!term) continue;
    let from = 0;
    while (from <= source.length - term.length) {
      const index = indexOfIgnoreCase(source, term, from);
      if (index === -1) break;
      if (isWordBoundary(source, index, term.length)) {
        matches.push({ index, length: term.length });
      }
      from = index + term.length;
    }
  }

  return matches.sort((a, b) => a.index - b.index);
}

export function textNamesTerm(text, terms) {
  return findTermMatches(text, terms).length > 0;
}

// Inserts the term at the caret, with spaces so it does not stick to nearby
// words. Returns the new value.
export function insertTerm(textarea, label) {
  const value = textarea.value || '';
  const start = textarea.selectionStart ?? value.length;
  const end = textarea.selectionEnd ?? start;
  const before = value.slice(0, start);
  const after = value.slice(end);
  const lead = before.length && !/\s$/.test(before) ? ' ' : '';
  const trail = /^\s/.test(after) ? '' : ' ';
  const insertion = `${lead}${label}${trail}`;

  textarea.value = `${before}${insertion}${after}`;
  const caret = before.length + insertion.length;
  textarea.setSelectionRange(caret, caret);
  return textarea.value;
}

// Drops any match that sits inside a longer one, so a term list of
// ["Google Docs", "docs"] takes out the whole phrase rather than leaving
// "Google" behind.
function widestMatches(matches) {
  return matches.filter(
    (match) =>
      !matches.some(
        (other) =>
          other !== match &&
          other.index <= match.index &&
          other.index + other.length >= match.index + match.length &&
          other.length > match.length
      )
  );
}

// Removes one occurrence of any of the terms (any casing), preferring the match
// nearest the caret, and collapses the double space it would otherwise leave.
export function removeTerm(textarea, terms) {
  const value = textarea.value || '';
  const matches = widestMatches(findTermMatches(value, terms));
  if (!matches.length) return false;

  const caret = textarea.selectionStart ?? value.length;
  let best = matches[0];
  let bestDistance = Math.abs(best.index - caret);
  for (const match of matches) {
    const distance = Math.abs(match.index - caret);
    if (distance < bestDistance) {
      best = match;
      bestDistance = distance;
    }
  }

  const before = value.slice(0, best.index);
  let after = value.slice(best.index + best.length);
  if (/\s$/.test(before) && /^\s/.test(after)) after = after.slice(1);

  textarea.value = `${before}${after}`;
  const nextCaret = Math.min(before.length, textarea.value.length);
  textarea.setSelectionRange(nextCaret, nextCaret);
  return true;
}
